import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";

/**
 * Converts a DOM element (or multi-page quotation elements) into a PDF File object.
 * @param {HTMLElement} element - The root container to capture.
 * @param {string} fileName - The desired PDF file name.
 * @param {object} options - Optional capture parameters.
 * @returns {Promise<File>} A PDF File object ready for attachments or download.
 */
export async function generatePdfFromElement(element, fileName = "document.pdf", options = {}) {
  if (!element) {
    throw new Error("Element to capture is required.");
  }

  const safeFileName = fileName.endsWith(".pdf") ? fileName : `${fileName.replace(/\.html$/i, "")}.pdf`;

  // Check if element contains multiple quotation pages
  const subPages = element.querySelectorAll ? Array.from(element.querySelectorAll(".quotation-page")) : [];
  const pagesToRender = subPages.length > 0 ? subPages : [element];

  const pdfDoc = await PDFDocument.create();

  // A4 standard points: 595.28 x 841.89 (72 points per inch)
  const A4_WIDTH = 595.28;
  const A4_HEIGHT = 841.89;

  const defaultOptions = {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    ...options,
  };

  for (const pageEl of pagesToRender) {
    const rawResult = typeof html2canvas === "function" ? html2canvas(pageEl, defaultOptions) : html2canvas.default(pageEl, defaultOptions);
    const canvas = await Promise.resolve(rawResult);

    if (!canvas || typeof canvas.toDataURL !== "function") {
      throw new Error("Failed to render element to canvas.");
    }

    const dataUrl = canvas.toDataURL("image/png");
    const base64Data = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
    const pngImage = await pdfDoc.embedPng(base64Data);

    const pdfPage = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
    pdfPage.drawImage(pngImage, {
      x: 0,
      y: 0,
      width: A4_WIDTH,
      height: A4_HEIGHT,
    });
  }

  const pdfBytes = await pdfDoc.save();
  return new File([pdfBytes], safeFileName, { type: "application/pdf" });
}
